<div class="min-h-screen w-full max-w-[100vw] overflow-x-clip flex flex-col bg-transparent text-slate-100 selection:bg-sky-500 selection:text-white">

  <!-- Skip to Content for WCAG AA Accessibility -->
  <a
    href="#main-content"
    class="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[999] focus:px-4 focus:py-2 focus:bg-sky-500 focus:text-slate-950 focus:font-bold focus:rounded-xl focus:shadow-2xl focus:outline-none">
    {{ 'common.skipToContent' | translate }}
  </a>

  <!-- Top Navigation Header -->
  <app-header></app-header>

  <!-- Main Content Area with Routing -->
  <main id="main-content" class="flex-1 w-full max-w-[100vw] overflow-x-clip pb-16 focus:outline-none" tabindex="-1">
    <router-outlet></router-outlet>
  </main>

  <!-- Offline Indicator Badge (MDN PWA Best Practice) -->
  @if (!pwaService.isOnline()) {
    <div
      role="status"
      aria-live="polite"
      class="fixed top-18 sm:top-20 right-4 sm:right-6 z-40 px-3.5 py-1.5 rounded-full glass-panel !border-amber-500/40 text-amber-300 text-xs font-semibold flex items-center gap-2 shadow-lg backdrop-blur-md animate-fade-in">
      <span class="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0"></span>
      <span>{{ 'pwa.offlineMode' | translate }}</span>
    </div>
  }

  <!-- Global Toast Notification (with mobile gesture safe-area inset) -->
  @if (pwaService.statusMessage(); as msgKey) {
    <div
      role="status"
      aria-live="polite"
      class="fixed bottom-[max(1.5rem,env(safe-area-inset-bottom,1.5rem))] left-1/2 -translate-x-1/2 z-[100] px-4 py-2.5 glass-dropdown text-white rounded-xl flex items-center gap-2.5 text-xs font-semibold luanti-dialog-panel shadow-2xl max-w-[90vw] text-center">
      <svg lucideCheck class="w-4 h-4 text-emerald-400 flex-shrink-0"></svg>
      <span>{{ msgKey | translate }}</span>
    </div>
  }

  <!-- PWA Automatic Update Banner (with mobile gesture safe-area inset) -->
  @if (pwaService.updateAvailable()) {
    <div
      role="alert"
      class="fixed bottom-[max(1.5rem,env(safe-area-inset-bottom,1.5rem))] right-4 sm:right-6 z-50 p-4 glass-btn-sky text-white rounded-2xl flex items-center gap-4 luanti-dialog-panel shadow-2xl max-w-[95vw]">
      <div class="text-xs font-medium">
        <p class="font-bold">{{ 'pwa.updateAvailable' | translate }}</p>
        <p class="text-sky-100">{{ 'pwa.updateDesc' | translate }}</p>
      </div>
      <button
        type="button"
        (click)="pwaService.applyUpdate()"
        class="px-3.5 py-1.5 bg-white text-sky-900 font-bold rounded-xl text-xs hover:bg-slate-100 transition shadow cursor-pointer whitespace-nowrap">
        {{ 'pwa.reloadNow' | translate }}
      </button>
    </div>
  }

  <!-- Global Unobtrusive Frosted-Glass Back-to-Top Button (Visible past 100vh on all pages) -->
  <app-back-to-top
    position="fixed"
    testId="page-back-to-top-btn"
    [visible]="showPageBackToTop()"
    [offsetBottomClass]="pwaService.updateAvailable() ? 'bottom-24 sm:bottom-24' : ''"
    (scrollToTop)="scrollToTopOfPage()">
  </app-back-to-top>

  <!-- Modals & Drawers (With Unified Enter & Exit Motion + Code-Split Lazy Chunks via @defer) -->
  @defer (when displayedPackage() !== null) {
    @if (displayedPackage(); as pkg) {
      <app-package-detail-modal
        [package]="pkg"
        [class.is-closing]="isClosingPackageModal()">
      </app-package-detail-modal>
    }
  }

  @defer (when renderSubmitModal()) {
    @if (renderSubmitModal()) {
      <app-submit-modal [class.is-closing]="isClosingSubmitModal()"></app-submit-modal>
    }
  }

  @defer (when renderSetupModal()) {
    @if (renderSetupModal()) {
      <app-setup-guide-modal [class.is-closing]="isClosingSetupModal()"></app-setup-guide-modal>
    }
  }

  @defer (when renderAuthModal()) {
    @if (renderAuthModal()) {
      <app-auth-modal [class.is-closing]="isClosingAuthModal()"></app-auth-modal>
    }
  }

  @defer (when renderProfileDrawer()) {
    @if (renderProfileDrawer()) {
      <app-profile-drawer [class.is-closing]="isClosingProfileDrawer()"></app-profile-drawer>
    }
  }

  @defer (when renderAuthorDrawer()) {
    @if (renderAuthorDrawer()) {
      <app-author-drawer [class.is-closing]="isClosingAuthorDrawer()"></app-author-drawer>
    }
  }

  <!-- Global Frosted Glass Overlay Preloader for OAuth Handshake & Profile Hydration -->
  @if (authService.isCompletingOAuth() || (authService.isProfileDrawerOpen() && !authService.currentUser() && authService.isLoading()) || (authService.isOAuthRedirecting() && !renderAuthModal())) {
    <app-frosted-preloader
      variant="overlay"
      size="lg"
      [label]="'auth.signingIn' | translate"
      [sublabel]="(authService.isOAuthRedirecting() ? 'auth.redirectingToProvider' : 'auth.preparingProfile') | translate">
    </app-frosted-preloader>
  }

  <!-- Widescreen 16:9 Cinema Video Theater Dialog (Enlarged from Hover Preview or Modal) -->
  @if (displayedVideoTheater(); as theater) {
    <div
      (click)="packageService.closeVideoTheater()"
      role="dialog"
      aria-modal="true"
      aria-labelledby="video-theater-title"
      [class.is-closing]="isClosingVideoTheater()"
      class="z-[60] flex items-center justify-center p-3 sm:p-6 overflow-y-auto luanti-modal-backdrop">
      <div
        (click)="$event.stopPropagation()"
        class="w-full max-w-5xl glass-modal rounded-3xl p-3 sm:p-5 space-y-3 sm:space-y-4 luanti-dialog-panel my-auto">

        <!-- Sleek Frosted Header Bar: Package Context + Quick Actions + Close -->
        <div class="flex items-center justify-between gap-3 flex-wrap px-1">
          <div class="flex items-center gap-3 min-w-0">
            <img
              [src]="theater.pkg.banner || theater.pkg.thumbnail || 'logo.svg'"
              [fallbackThumbnail]="theater.pkg.thumbnail || ''"
              appImgFallback
              [alt]="theater.pkg.title"
              class="w-9 h-9 sm:w-10 sm:h-10 rounded-xl object-cover bg-slate-900 border border-white/15 flex-shrink-0"
            />
            <div class="min-w-0">
              <h2 id="video-theater-title" class="text-sm sm:text-base font-extrabold text-white truncate">
                {{ theater.pkg.title }}
              </h2>
              <p class="text-[11px] text-slate-400 truncate">
                {{ 'card.byAuthor' | translate }} <span class="text-sky-400 font-mono">&#64;{{ theater.pkg.author }}</span>
              </p>
            </div>
          </div>

          <div class="flex items-center gap-2 ml-auto">
            @if (!packageService.activePackage() || packageService.activePackage()?.id !== theater.pkg.id) {
              <button
                type="button"
                (click)="packageService.openPackageFromVideoTheater()"
                class="px-3 py-1.5 rounded-xl glass-btn text-slate-100 hover:text-white text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer">
                <svg lucideInfo class="w-3.5 h-3.5 text-sky-400"></svg>
                <span class="hidden xs:inline">{{ 'card.viewPackageDetails' | translate }}</span>
              </button>
            }

            <button
              type="button"
              (click)="packageService.downloadPackage(theater.pkg)"
              class="px-3.5 py-1.5 rounded-xl glass-btn-primary text-white text-xs font-bold flex items-center gap-1.5 transition hover:scale-105 cursor-pointer">
              <svg lucideDownload class="w-3.5 h-3.5"></svg>
              <span>{{ 'card.downloadZip' | translate }}</span>
              @if (theater.pkg.file_size) {
                <span class="text-[10px] text-sky-100 hidden sm:inline">({{ theater.pkg.file_size }})</span>
              }
            </button>

            <button
              type="button"
              (click)="packageService.closeVideoTheater()"
              [attr.aria-label]="'modal.close' | translate"
              class="p-2 rounded-xl glass-btn text-slate-200 hover:text-white flex items-center justify-center transition hover:scale-105 active:scale-95 cursor-pointer">
              <svg lucideX class="w-4 h-4 sm:w-5 sm:h-5"></svg>
            </button>
          </div>
        </div>

        <!-- 16:9 Widescreen Cinema Stage -->
        <div class="relative w-full aspect-video rounded-2xl overflow-hidden bg-black border border-white/10 shadow-2xl">
          @if (packageService.isDirectVideoUrl(theater.pkg.video_url)) {
            <video
              [src]="theater.pkg.video_url"
              [poster]="theater.pkg.video_thumbnail_url || theater.pkg.banner || theater.pkg.thumbnail"
              (loadedmetadata)="onTheaterVideoLoadedMetadata($event, theater.startSeconds)"
              preload="auto"
              controls
              autoplay
              playsinline
              class="w-full h-full object-contain bg-black">
            </video>
          } @else if (theaterSafeEmbedUrl(); as safeTheaterEmbed) {
            <iframe
              [src]="safeTheaterEmbed"
              [title]="theater.pkg.title + ' video'"
              class="w-full h-full border-0 bg-black"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowfullscreen>
            </iframe>
          }
        </div>

      </div>
    </div>
  }

  <!-- Official Luanti Blog Article Reader Dialog (Matched to Package Detail Dialog Layout & Width) -->
  @if (renderBlogArticleModal() && (packageService.activeBlogArticle() || displayedBlogArticle()); as blogPost) {
    <div
      (click)="packageService.closeBlogArticleModal()"
      role="dialog"
      aria-modal="true"
      aria-labelledby="blog-article-modal-title"
      [class.is-closing]="isClosingBlogArticleModal()"
      class="z-50 flex items-center justify-center p-0 sm:p-4 overflow-y-auto luanti-modal-backdrop">
      <div
        (click)="$event.stopPropagation()"
        class="relative w-full max-w-5xl xl:max-w-[1120px] max-h-screen sm:max-h-[94vh] glass-modal rounded-none sm:rounded-3xl overflow-hidden flex flex-col luanti-dialog-panel my-auto">

        <div class="overflow-y-auto flex-1 flex flex-col luanti-modal-scroll">
          <!-- Top Banner Hero (True 16:9 Widescreen Aspect Ratio, Matched to Package Detail Dialog) -->
          <div class="relative w-full aspect-video min-h-[260px] sm:min-h-[380px] lg:min-h-[480px] max-h-[630px] bg-slate-950 flex-shrink-0 overflow-hidden">
            <img
              [src]="blogPost.image_url || '/banners/luanti-5-18-0.png'"
              [fallbackThumbnail]="'/banners/luanti-5-18-0.png'"
              appImgFallback
              [alt]="blogPost.title"
              decoding="async"
              class="w-full h-full object-cover object-center"
            />
            <div class="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/30 to-transparent w-full sm:w-2/3"></div>
            <div class="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/20 to-transparent"></div>

            <!-- Floating Frosted Glass Close Button (Top Right) -->
            <button
              type="button"
              (click)="packageService.closeBlogArticleModal()"
              [attr.aria-label]="'modal.close' | translate"
              class="absolute top-4 right-4 p-2 rounded-xl glass-btn text-slate-200 hover:text-white flex items-center justify-center transition hover:scale-105 active:scale-95 z-30 cursor-pointer">
              <svg lucideX class="w-4 h-4 sm:w-5 sm:h-5"></svg>
            </button>

            <!-- Foreground Hero Content (Matched to Hero & Package Detail Dialog) -->
            <div class="absolute bottom-5 left-5 sm:left-8 right-5 sm:right-8 flex flex-col justify-end gap-3 z-20">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider glass-badge-sky shadow-[0_0_16px_rgba(56,189,248,0.25)]">
                  LUANTI BLOG{{ formattedBlogArticleDate() ? ' · ' + (formattedBlogArticleDate() | uppercase) : '' }}
                </span>
                @if (blogPost.author) {
                  <span class="glass-badge glass-badge-sky font-mono">
                    {{ 'card.byAuthor' | translate }} {{ blogPost.author.includes(' ') ? blogPost.author : '&#64;' + blogPost.author }}
                  </span>
                }
              </div>
              <h2 id="blog-article-modal-title" class="text-2xl sm:text-4xl lg:text-5xl font-black text-white drop-shadow-2xl">
                {{ blogPost.title }}
              </h2>
            </div>
          </div>

          <!-- Formatted Blog Article Content Body (Matched to Package Detail Dialog Padding & Panel Style) -->
          <div class="p-6 sm:p-8 lg:p-10 space-y-7">
            <div class="p-5 sm:p-7 rounded-2xl glass-panel">
              <article
                class="luanti-markdown luanti-blog-article text-slate-200 text-sm sm:text-base leading-relaxed space-y-3 break-words"
                [innerHTML]="safeBlogArticleHtml()">
              </article>
            </div>
          </div>
        </div>

      </div>
    </div>
  }

  <!-- Official Legal, DMCA & Safe Harbor Modal (/legal) -->
  @if (renderLegalModal()) {
    <div
      (click)="packageService.closeLegalModal()"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-modal-title"
      [class.is-closing]="isClosingLegalModal()"
      class="z-50 flex items-center justify-center p-4 overflow-y-auto luanti-modal-backdrop">
      <div
        (click)="$event.stopPropagation()"
        class="w-full max-w-2xl glass-modal rounded-3xl p-6 sm:p-8 space-y-5 luanti-dialog-panel my-auto text-xs text-slate-300 leading-relaxed">
        <div class="flex items-center justify-between border-b border-white/10 pb-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center text-emerald-400">
              <svg lucideShieldCheck class="w-5 h-5"></svg>
            </div>
            <div>
              <h2 id="legal-modal-title" class="text-lg font-bold text-white">
                {{ 'footer.legalModalTitle' | translate }}
              </h2>
              <p class="text-xs text-slate-400">LuantiHub Open-Source Governance &amp; Safe Harbor</p>
            </div>
          </div>
          <button
            type="button"
            (click)="packageService.closeLegalModal()"
            aria-label="Close legal modal"
            class="p-2 rounded-xl glass-btn text-slate-200 hover:text-white flex items-center justify-center transition hover:scale-105 active:scale-95 cursor-pointer">
            <svg lucideX class="w-4 h-4 sm:w-5 sm:h-5"></svg>
          </button>
        </div>

        <div class="space-y-4">
          <div class="p-4 rounded-2xl glass-panel space-y-1.5">
            <h3 class="font-bold text-white text-sm">1. Official Trademark &amp; Non-Affiliation Disclaimer</h3>
            <p class="text-slate-300">{{ 'footer.disclaimer' | translate }}</p>
          </div>

          <div class="p-4 rounded-2xl glass-panel space-y-1.5">
            <h3 class="font-bold text-white text-sm">2. DMCA § 512 &amp; EU Digital Services Act (DSA) Safe Harbor</h3>
            <p class="text-slate-300">{{ 'footer.legalSafeHarbor' | translate }}</p>
            <p class="text-slate-400 text-[11px] pt-1">
              Use the in-app Flag / Report button on any package detail dialog for immediate moderation review.
            </p>
          </div>

          <div class="p-4 rounded-2xl glass-panel space-y-1.5">
            <h3 class="font-bold text-white text-sm">3. SPDX Open-Source Licensing &amp; Privacy</h3>
            <p class="text-slate-300">{{ 'footer.legalPrivacy' | translate }}</p>
          </div>

          <div
            data-testid="legal-account-deletion-section"
            class="p-4 rounded-2xl bg-rose-950/20 border border-rose-500/30 space-y-2.5">
            <h3 class="font-bold text-rose-300 text-sm flex items-center gap-1.5">
              <svg lucideTrash class="w-4 h-4 text-rose-400 shrink-0"></svg>
              <span>{{ 'footer.accountDeletionTitle' | translate }}</span>
            </h3>
            <p class="text-slate-200 leading-relaxed">{{ 'footer.accountDeletionSteps' | translate }}</p>
            <p class="text-slate-400 text-[11px] leading-relaxed">{{ 'footer.accountDeletionDataSpec' | translate }}</p>
            <div class="pt-1">
              <button
                type="button"
                data-testid="legal-delete-account-btn"
                (click)="onOpenAccountDeletionFromLegal()"
                class="px-3.5 py-2 rounded-xl glass-btn-rose text-white font-bold text-xs transition inline-flex items-center gap-1.5 cursor-pointer">
                <svg lucideTrash class="w-3.5 h-3.5"></svg>
                <span>{{ 'footer.accountDeletionBtn' | translate }}</span>
              </button>
            </div>
          </div>
        </div>

        <div class="flex justify-end pt-2">
          <button
            type="button"
            (click)="packageService.closeLegalModal()"
            class="px-5 py-2 rounded-xl glass-btn-sky text-white font-bold text-xs transition cursor-pointer">
            {{ 'common.close' | translate }}
          </button>
        </div>
      </div>
    </div>
  }

  <!-- Global Footer with Official Legal & Safe Harbor Disclaimers -->
  <footer class="mt-auto border-t border-white/10 bg-slate-950/90 backdrop-blur-md py-10 text-slate-400 text-xs">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
      <!-- Row 1: Brand Identity & Full Tagline -->
      <div class="flex flex-wrap items-center gap-2.5">
        <img src="logo.svg" alt="LuantiHub" width="24" height="24" class="w-6 h-6 rounded-lg flex-shrink-0" />
        <span class="font-bold text-white text-sm tracking-tight">Luanti<span class="text-sky-400">Hub</span></span>
        <span class="text-slate-400">•</span>
        <span class="text-slate-400 text-xs">{{ 'footer.about' | translate }}</span>
      </div>

      <!-- Row 2: Full-Width Evenly Spaced Navigation Links Row -->
      <nav aria-label="Footer navigation" class="w-full flex flex-wrap items-center justify-between gap-x-5 gap-y-2.5 pt-3.5 border-t border-white/10 text-xs whitespace-nowrap">
        <a href="/games" (click)="onFooterNavClick($event, 'game')" class="hover:text-sky-400 transition cursor-pointer no-underline">
          {{ 'header.filterGames' | translate }}
        </a>
        <a href="/mods" (click)="onFooterNavClick($event, 'mod')" class="hover:text-sky-400 transition cursor-pointer no-underline">
          {{ 'header.filterMods' | translate }}
        </a>
        <a href="/texture-packs" (click)="onFooterNavClick($event, 'txp')" class="hover:text-sky-400 transition cursor-pointer no-underline">
          {{ 'header.filterTxp' | translate }}
        </a>
        <a href="/git" (click)="onFooterNavClick($event, 'git')" class="hover:text-sky-400 transition cursor-pointer no-underline">
          {{ 'header.filterGit' | translate }}
        </a>
        <a href="/setup" (click)="onFooterNavClick($event, 'setup')" class="hover:text-sky-400 transition cursor-pointer no-underline">
          {{ 'footer.inGameSetup' | translate }}
        </a>
        <a href="/submit" (click)="onFooterNavClick($event, 'submit')" class="hover:text-sky-400 transition cursor-pointer no-underline">
          {{ 'header.submitRepo' | translate }}
        </a>
        <a href="https://content.luanti.org" target="_blank" rel="noopener noreferrer" class="hover:text-sky-400 transition cursor-pointer">
          {{ 'footer.upstreamMirror' | translate }}
        </a>
        <a href="https://www.luanti.org" target="_blank" rel="noopener noreferrer" class="hover:text-sky-400 transition cursor-pointer">
          {{ 'footer.engineSite' | translate }}
        </a>
        <a
          href="https://github.com/sakel-hub/luanti-hub/issues/new/choose"
          target="_blank"
          rel="noopener noreferrer"
          class="hover:text-sky-400 transition cursor-pointer flex items-center gap-1.5">
          <svg class="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24" aria-hidden="true">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"></path>
          </svg>
          {{ 'footer.reportIssue' | translate }}
        </a>
        <a href="/legal" (click)="onFooterNavClick($event, 'legal')" class="hover:text-sky-400 transition font-semibold text-slate-300 cursor-pointer no-underline">
          {{ 'footer.termsPrivacy' | translate }}
        </a>
      </nav>

      <!-- Legal & Safe Harbor Columns -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-white/5 text-[11px] text-slate-400 leading-relaxed">
        <div>
          <strong class="text-slate-300 block mb-1">Official Trademark &amp; Licensing</strong>
          <p>{{ 'footer.disclaimer' | translate }}</p>
        </div>
        <div>
          <strong class="text-slate-300 block mb-1">DMCA § 512 &amp; EU DSA Safe Harbor</strong>
          <p>{{ 'footer.legalSafeHarbor' | translate }}</p>
        </div>
        <div>
          <strong class="text-slate-300 block mb-1">Zero-Tracking Privacy &amp; Edge Security</strong>
          <p>{{ 'footer.legalPrivacy' | translate }}</p>
        </div>
      </div>

      <!-- Row 4: Copyright, Open-Source License & Engine Trademark Attribution -->
      <div class="pt-4 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-[11px] text-slate-400">
        <p>
          &copy; {{ currentYear }} <a href="https://luantihub.org" class="text-slate-300 hover:text-sky-400 font-semibold transition">LuantiHub</a> &bull; {{ 'footer.copyright' | translate }}
        </p>
        <p class="text-slate-400">
          {{ 'footer.trademarkNotice' | translate }}
        </p>
      </div>
    </div>
  </footer>

</div>
